import DateTimePicker from '@/components/Form/DateTimePicker'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { getCustomerCredits } from '@/modules/Customer/components/Detail/Credits/services'
import { TRANSACTION_PAYMENT_METHODS } from '@/modules/Invoice/constants'
import {
    createInvoiceTransaction,
    getInvoicePaymentTotal,
} from '@/modules/Invoice/services'
import { InvoiceDetail, TransactionParams } from '@/modules/Invoice/types'
import { ModalProps } from '@/types/shared'
import { formatCurrency, formatPGDate } from '@/utils/function'
import { getKpiPaymentCutoffMinDate } from '@/utils/kpi-payment-cutoff'
import dayjs from 'dayjs'
import { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Dialog, GridHookForm } from 'vnetwork-ui'

export interface CreateCreditModalProps extends ModalProps {
    invoice: InvoiceDetail
    totalPaid: number
}

const CreateCreditModal = ({
    onClose,
    open,
    invoice,
}: CreateCreditModalProps) => {
    const { t } = useTranslation()

    // After 17:30 on the 2nd of each month, restrict payment date to current month only
    // (KPI payment file is finalized at that time for commission calculation)
    const kpiCutoffMinDate = useMemo(() => getKpiPaymentCutoffMinDate(), [])

    const { data: credits, renderState: renderCreditsState } =
        useQueryWithHandler({
            queryKey: [
                QUERY_KEYS.CUSTOMERS_CREDITS,
                invoice?.customer?.customer_id,
            ],
            queryFn: () =>
                getCustomerCredits({
                    customerID: invoice.customer.customer_id,
                }),
        })

    const { data, renderState: renderPaymentState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.INVOICES_PAYMENT, invoice?.invoice_id],
        queryFn: () =>
            getInvoicePaymentTotal({ invoiceID: invoice?.invoice_id }),
    })

    const formControl = useForm<
        TransactionParams & { credit_id: number; invoice_payment_at: string }
    >({
        defaultValues: {
            credit_id: credits?.results?.[0]?.credit_id || -1,
            invoice_id: invoice?.invoice_id,
            invoice_transaction_before_exchange_rate:
                invoice?.invoice_total - (data?.result?.invoice_paid || 0),
            transaction_fee: 0,
            currency_of_invoice: data?.result?.invoice_currency || 'VND',
            transaction_note: '',
            transaction_method: 'bank-transfer',
            invoice_payment_at: dayjs().format('YYYY-MM-DD HH:mm:ss'),
            currency: 'VND',
            exchange_rate: data?.result?.exchange_rate || 1,
        },
    })

    const { mutateAsync, isPending } = useMutationWithHandler(
        { mutationFn: createInvoiceTransaction },
        {
            reactQueryKeys: QUERY_KEYS.INVOICES,
            onSuccess: () => {
                onClose()
            },
        }
    )

    const handleCreate = (
        values: TransactionParams & {
            credit_id: number
            invoice_payment_at: any
        }
    ) => {
        // DateTimePicker returns Dayjs object; format to string for API
        const paymentAt = dayjs.isDayjs(values.invoice_payment_at)
            ? values.invoice_payment_at.format('YYYY-MM-DD')
            : formatPGDate(values.invoice_payment_at, 'YYYY-MM-DD') || ''

        mutateAsync({
            invoiceID: invoice.invoice_id,
            data: {
                ...values,
                invoice_payment_at: paymentAt,
            },
        })
    }

    const creditsStateComponent = renderCreditsState()
    if (creditsStateComponent) return creditsStateComponent

    const paymentStateComponent = renderPaymentState()
    if (paymentStateComponent) return paymentStateComponent

    return (
        <Dialog
            title={t('invoices.transactions.create')}
            onClose={onClose}
            open={open}
        >
            <GridHookForm
                formControl={formControl}
                actions={[
                    {
                        title: t('cancel'),
                        type: 'button',
                        variant: 'outlined',
                        handler: onClose,
                        disabled: isPending,
                    },
                    {
                        title: t('invoices.transactions.create'),
                        type: 'submit',
                        variant: 'contained',
                        loading: isPending,
                        disabled: isPending,
                    },
                ]}
                onSubmit={handleCreate}
                data={[
                    {
                        formItemProps: {
                            name: 'transaction_method',
                            type: 'select',
                            label: t('transaction_method'),
                            placeholder: t('transaction_method'),
                            options: TRANSACTION_PAYMENT_METHODS(t)?.filter(
                                (method) => {
                                    if (invoice?.invoice_type === 'deposit') {
                                        return method?.value !== 'credit'
                                    }
                                    return true
                                }
                            ),
                        },

                        className: 'col-span-4',
                    },
                    {
                        formItemProps: {
                            name: 'credit_id',
                            type: 'select',
                            label: t('transaction.credit_id'),
                            placeholder: t('transaction.credit_id'),
                            options: credits?.results?.map((credit) => {
                                return {
                                    value: credit?.credit_id,
                                    label: `${credit?.credit_name} - ${formatCurrency(credit?.credit_remaining)}`,
                                }
                            }),
                        },
                        className: `${formControl.watch('transaction_method') === 'credit' ? 'col-span-4' : 'hidden'}`,
                    },
                    {
                        formItemProps: {
                            name: 'invoice_transaction_before_exchange_rate',
                            type: 'number',
                            label: t('transaction_amount', { ns: 'invoice' }),
                            placeholder: t('transaction_amount', {
                                ns: 'invoice',
                            }),
                        },
                        className: 'col-span-4 md:col-span-3',
                    },
                    {
                        formItemProps: {
                            name: 'currency',
                            type: 'select',
                            label: t('currency_of_transaction'),
                            placeholder: t('currency_of_transaction'),
                            // USD -> only USD, VND -> [VND, USD, EUR, JPY]
                            options:
                                formControl?.watch('currency_of_invoice') &&
                                formControl?.watch('currency_of_invoice') !==
                                    'VND'
                                    ? [{ label: 'USD', value: 'USD' }]
                                    : [
                                          { label: 'VND', value: 'VND' },
                                          { label: 'USD', value: 'USD' },
                                          { label: 'EUR', value: 'EUR' },
                                          { label: 'JPY', value: 'JPY' },
                                      ],
                            allowClear: false,
                        },
                        className: 'col-span-4 md:col-span-1',
                    },
                    {
                        formItemProps: {
                            name: 'currency_of_invoice',
                            type: 'select',
                            label: t('currency_of_invoice', { ns: 'invoice' }),
                            placeholder: t('currency_of_invoice', {
                                ns: 'invoice',
                            }),
                            options: [
                                { label: 'VND', value: 'VND' },
                                { label: 'USD/ USDT', value: 'USD' },
                                { label: 'EUR', value: 'EUR' },
                                { label: 'JPY', value: 'JPY' },
                            ],
                            allowClear: false,
                        },
                        className: 'col-span-4',
                    },

                    {
                        formItemProps: {
                            name: 'exchange_rate',
                            type: 'number',
                            label: t('exchange_rate'),
                            placeholder: t('exchange_rate'),
                        },
                        className: 'col-span-4',
                    },
                    {
                        renderEditComponent: () => (
                            <div>
                                <DateTimePicker
                                    name="invoice_payment_at"
                                    control={formControl.control}
                                    label={t('invoice_payment_at')}
                                    placeholder={t('invoice_payment_at')}
                                    minDate={kpiCutoffMinDate ?? undefined}
                                    includeTime={false}
                                />
                                {kpiCutoffMinDate && (
                                    <p className="mt-1 text-xs text-amber-600">
                                        {t('kpi_payment_cutoff_warning', {
                                            ns: 'invoice',
                                        })}
                                    </p>
                                )}
                            </div>
                        ),
                        className: 'col-span-4',
                    },
                    {
                        formItemProps: {
                            name: 'transaction_fee',
                            type: 'number',
                            label: t('transaction_fee'),
                            placeholder: t('transaction_fee'),
                            helperText: t('transaction_fee_note', {
                                ns: 'invoice',
                            }),
                        },
                        className: 'col-span-4',
                    },
                    {
                        formItemProps: {
                            name: 'transaction_note',
                            type: 'textarea',
                            label: t('transaction_note'),
                            placeholder: t('transaction_note'),
                        },
                        className: 'col-span-4',
                    },
                ]}
            />
        </Dialog>
    )
}

export default CreateCreditModal
